/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { MAX_ALLOWED_SECTIONS, MAX_MENU_HEIGHT } from '../../../constants';
import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type { NamedItems } from '../../../types';
import { getFoldableThreshold, hasMoreButton } from '../MenuSectionPrimitive';
import NativeMenuSection from '../NativeMenuSection';
import SuggestionMenuSection from '../SuggestionMenuSection';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

function MenuContent() {
	const { mixedItems, suggestionConfig } = useMenuControl();
	const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');
	const { fireNewNativeMenuViewed_old, fireNewNativeMenuViewed } = useUniversalCreateAnalytics();

	const analyticsFired = useRef(false);

	// Check if any menu items are currently loading
	const hasLoadingItems = mixedItems.some((section) =>
		section.items.some((item) => item.isLoading === true),
	);

	const sectionsToAnalyze = mixedItems.slice(0, MAX_ALLOWED_SECTIONS);

	useEffect(() => {
		if (mixedItems.length > 0 && !hasLoadingItems && !analyticsFired.current) {
			const totalNativeItems = sectionsToAnalyze.reduce(
				(total, section) => total + section.items.length,
				0,
			);

			let unnamedSectionIndex = 0;

			if (fg('navx-2236-unified-instrumentation-updates')) {
				const sections = sectionsToAnalyze.map((namedItem, sectionIndex) => {
					const moreButtonEnabled = hasMoreButton(namedItem.foldable, namedItem.items.length);
					return {
						groupHeading: namedItem.heading || `native-section-${unnamedSectionIndex++}`,
						groupIndex: sectionIndex, // Index of section out of all sections
						groupItemCount: namedItem.items.length, // total number of items in the section
						groupItemAtisList: moreButtonEnabled
							? namedItem.items
									.slice(0, getFoldableThreshold(namedItem.foldable))
									.map((item) => item.ati.toString())
							: namedItem.items.map((item) => item.ati.toString()),
						inMoreItemAtisList: moreButtonEnabled
							? namedItem.items
									.slice(getFoldableThreshold(namedItem.foldable))
									.map((item) => item.ati.toString())
							: [],
						inMoreItemCount: moreButtonEnabled
							? namedItem.items.length - getFoldableThreshold(namedItem.foldable)
							: 0,
						showMoreButton: moreButtonEnabled, // true if items in section have been truncated with show more button
					};
				});

				fireNewNativeMenuViewed({
					crossAppSuggestionsEnabled: crossAppCreationEnabled,
					nativeGroupCount: sectionsToAnalyze.length,
					nativeItemCount: totalNativeItems,
					nativeMenuGroups: sections,
				});
			} else {
				const sections = sectionsToAnalyze.map((namedItem, sectionIndex) => ({
					groupHeading: namedItem.heading || `native-section-${unnamedSectionIndex++}`,
					groupIndex: sectionIndex, // Index of section out of all sections
					groupItemCount: namedItem.items.length, // total number of items in the section
					itemAtis: namedItem.items.map((item) => item.ati.toString()), // Atis of items in the section
					showMoreButton: hasMoreButton(namedItem.foldable, namedItem.items.length), // true if items in section have been truncated with show more button
				}));

				fireNewNativeMenuViewed_old({
					crossAppSuggestionsEnabled: crossAppCreationEnabled,
					totalGroups: sectionsToAnalyze.length,
					totalNativeItems,
					nativeMenuGroups: sections,
				});
			}

			analyticsFired.current = true;
		}
	}, [
		mixedItems,
		hasLoadingItems,
		crossAppCreationEnabled,
		fireNewNativeMenuViewed,
		fireNewNativeMenuViewed_old,
		sectionsToAnalyze,
	]);

	// Reset analytics when loading state changes
	useEffect(() => {
		if (hasLoadingItems) {
			analyticsFired.current = false;
		}
	}, [hasLoadingItems]);

	if (!mixedItems.length) {
		return null;
	}

	const isSuggestionSectionEnabled = crossAppCreationEnabled && suggestionConfig.enabled;
	// including a check for enabled here ensures we avoid error TS2339 Property location does not exist on type SuggestionConfigDisabled
	const showSuggestionsAtStart =
		suggestionConfig?.enabled && suggestionConfig?.location === 'start';

	return (
		<MenuGroup
			spacing="compact"
			testId="global-create-content-popover-menu"
			{...(sectionsToAnalyze.some((section) => section.foldable === 'enhanced') &&
			fg('navx_universal_create_overflow_mode')
				? { maxHeight: MAX_MENU_HEIGHT }
				: undefined)}
		>
			<Box xcss={cx(styles.menuWrap)}>
				{isSuggestionSectionEnabled && showSuggestionsAtStart && <SuggestionMenuSection />}
				{sectionsToAnalyze.map((namedItem: NamedItems, sectionIndex: number) => {
					const totalNativeItems = sectionsToAnalyze.reduce(
						(total, section) => total + section.items.length,
						0,
					);

					// Calculate the starting index for this section by summing items from all previous sections
					const sectionStartIndex = sectionsToAnalyze
						.slice(0, sectionIndex)
						.reduce((total, section) => total + section.items.length, 0);

					return (
						<NativeMenuSection
							namedItems={namedItem}
							sectionIndex={
								isSuggestionSectionEnabled && showSuggestionsAtStart && !suggestionConfig.compact
									? sectionIndex + 1
									: sectionIndex
							}
							totalMenuItemsCount={totalNativeItems}
							sectionStartIndex={sectionStartIndex}
							key={`menu-section-${sectionIndex}`}
						/>
					);
				})}
				{isSuggestionSectionEnabled && !showSuggestionsAtStart && <SuggestionMenuSection />}
			</Box>
		</MenuGroup>
	);
}

export default withIntlProvider(MenuContent);
